import React from "react";
import { Mail, List, Palette, LayoutTemplate, Plus } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";

import EmailTemplatesScreen from "../../../modules/Admin/AdminEmailTemplates/EmailTemplatesScreen";
import BrandingProfilesScreen from "../../../modules/Admin/AdminEmailTemplates/BrandingProfilesScreen";
import EmailLayoutsScreen from "../../../modules/Admin/AdminEmailTemplates/EmailLayoutsScreen";
import EmailCategoriesScreen from "../../../modules/Admin/AdminEmailTemplates/EmailCategoriesScreen";
const EmailManagementScreen: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const tabs = [
    {
      key: "templates",
      label: "Email Templates",
      icon: Mail,
      path: "/admin/adminemailtemplates/EmailTemplates",
    },
    {
      key: "categories",
      label: "Email Categories",
      icon: List,
      path: "/admin/adminemailtemplates/categories",
    },
    {
      key: "branding",
      label: "Branding Profiles",
      icon: Palette,
      path: "/admin/adminemailtemplates/branding",
    },
    {
      key: "layouts",
      label: "Email Layouts",
      icon: LayoutTemplate,
      path: "/admin/adminemailtemplates/layouts",
    },
  ];

  const activeTab =
    tabs.find((tab) => location.pathname === tab.path) ??
    tabs.find((tab) => location.pathname.startsWith(`${tab.path}/`)) ??
    tabs[0];

  const handleTabChange = (path: string) => {
    navigate(path);
  };

  const handleNew = () => {
    switch (activeTab.key) {
      case "templates":
        navigate("/admin/adminemailtemplates/create");
        break;

      case "categories":
        navigate("/admin/adminemailtemplates/categories/create");
        break;

      case "branding":
        navigate("/admin/adminemailtemplates/branding/create");
        break;

      case "layouts":
        navigate("/admin/adminemailtemplates/layouts/create");
        break;

      default:
        break;
    }
  };

  const getNewButtonText = () => {
    switch (activeTab.key) {
      case "templates":
        return "New Email Template";

      case "categories":
        return "New Category";

      case "branding":
        return "New Branding Profile";

      case "layouts":
        return "New Email Layout";

      default:
        return "New";
    }
  };

  return (
    <div className="min-h-full bg-slate-50/40">
      {/* ========================================================= */}
      {/* PAGE HEADER */}
      {/* ========================================================= */}

      <div className="border-b border-slate-200 bg-white">
        <div className="px-6 pt-5">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-slate-900">
                Email Management
              </h1>

              <p className="mt-1 text-sm text-slate-500">
                Manage email categories, branding profiles, layouts and
                templates.
              </p>
            </div>

            <button
              type="button"
              onClick={handleNew}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-[#2E7D32] px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-[#256B2A] focus:outline-none focus:ring-2 focus:ring-[#2E7D32]/20"
            >
              <Plus size={17} />

              {getNewButtonText()}
            </button>
          </div>

          {/* ========================================================= */}
          {/* TABS */}
          {/* ========================================================= */}

          <div className="mt-5 flex gap-1 overflow-x-auto">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab.key === tab.key;

              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => handleTabChange(tab.path)}
                  className={`group relative flex shrink-0 items-center gap-2 px-4 py-3 text-sm font-medium transition ${
                    isActive
                      ? "text-primary"
                      : "text-slate-500 hover:text-slate-800"
                  }`}
                >
                  <Icon
                    size={16}
                    className={
                      isActive
                        ? "text-primary"
                        : "text-slate-400 group-hover:text-slate-600"
                    }
                  />

                  <span>{tab.label}</span>

                  {isActive && (
                    <span className="absolute inset-x-2 bottom-0 h-0.5 rounded-full bg-primary" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* ========================================================= */}
      {/* CONTENT */}
      {/* ========================================================= */}

      <div className="p-6">
        {activeTab.key === "templates" && <EmailTemplatesScreen />}

        {activeTab.key === "categories" && <EmailCategoriesScreen />}

        {activeTab.key === "branding" && <BrandingProfilesScreen />}

        {activeTab.key === "layouts" && <EmailLayoutsScreen />}
      </div>
    </div>
  );
};

interface ComingSoonProps {
  icon: React.ElementType;
  title: string;
  description: string;
}

const ComingSoon: React.FC<ComingSoonProps> = ({
  icon: Icon,
  title,
  description,
}) => {
  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="flex min-h-[360px] flex-col items-center justify-center p-8 text-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <Icon size={25} />
        </div>

        <h2 className="mt-4 text-lg font-semibold text-slate-900">{title}</h2>

        <p className="mt-1 max-w-md text-sm text-slate-500">{description}</p>

        <span className="mt-4 rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-500">
          Coming next
        </span>
      </div>
    </div>
  );
};

export default EmailManagementScreen;
